import React, { useState, useRef, useContext } from 'react';
import { Container, Col, Form, Badge, Row } from 'react-bootstrap';
import IntlCurrencyInput from 'react-intl-currency-input';
import styled, { css } from 'styled-components';
import InputMask from 'react-input-mask';
import Avatar from '../Global/Avatar';
import { OptionContext } from '../../contexts/OptionContext';
import RobotBox from '../layout/RobotBox';
import ButtonInputType from '../layout/ButtonInputType';

const currencyConfig = {
  locale: 'pt-BR',
  formats: {
    number: {
      BRL: {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    },
  },
};

const FormControlCustom = styled(Form.Control)`${(props) => css`
    color: ${props.foreground};
    ::placeholder {
      color: ${props.foreground};
      opacity: 0.7;
    }
    &:hover {
      color: ${props.foreground};
    }
    :focus {
      color: ${props.foreground};
    }
  `
}`;

const BadgeCustom = styled(Badge)`
  ${(props) => css`
      color: ${props.foreground};
      background: ${props.background};
      min-width: 30px;
      font-weight: bold;
      font-size: 75%;
      padding: 0.5rem 1rem !important;
      border-radius: 10px;
      cursor: pointer; 
  `}
`;

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => htmlElRef.current && htmlElRef.current.focus();

  return [htmlElRef, setFocus];
};

const nextDefault = () => { };

const TextInput = ({
  suggestions = [],
  maxSuggestions = 15,
  placeholder = '',
  mask = '',
  validation = 'text',
  filtered = false,
  minLength = 0,
  numberMode = false,
  textareaMode = false,
  optional = false,
  onSuggestionSubmit = false,
  defaultData = '',
  next = nextDefault }) => {
  const [inputRef, setInputFocus] = useFocus();
  const [filterSuggestions, setFilterSuggestions] = useState(suggestions);
  const [value, setValue] = useState(defaultData);
  const [submitValue, setSubmitValue] = useState(defaultData);
  const form = useRef();

  const optionContext = useContext(OptionContext);
  const { input, dialog } = optionContext;

  const continueAction = (e) => {
    e.preventDefault();
    if (isValid()) {
      if (mask) {
        next(submitValue);
      } else {
        next(submitValue);
      }
    }
  };

  const continueWithSuggestion = (nextValue) => {
    next(nextValue);
  };

  const strNormalize = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filterSuggestion = (val) => {
    const newFilter = suggestions.filter((f) => (f.value !== undefined
      && strNormalize(f.value).includes(strNormalize(val)))
      || (f.text !== undefined && f.text.toLowerCase().includes(val.toLowerCase())));
    setFilterSuggestions(newFilter);
  };

  const handleChangeCurrency = (event, val, maskedValue) => {
    const valObject = {
      raw: val,
      maskedValue,
    };
    setValue(valObject);
    setSubmitValue(valObject);

    if (onSuggestionSubmit) {
      continueWithSuggestion(valObject);
    }
  };

  const isValid = () => {
    if (validation === 'phone') {
      return value.length === 15;
    }
    return value !== '' && value !== 0;
  };

  const handleChange = (event) => {
    setValue(event.target.value);
    setSubmitValue(event.target.value);
    if (filtered) {
      filterSuggestion(event.target.value);
    }
  };

  return (
    <div className="fixed-bottom mb-lg-4">
      {(suggestions && suggestions.length > 0 && (value.length >= minLength || minLength === 0))
        && (
          <Container fluid="lg" className="py-2 bg-white" style={{ overflow: 'auto', whiteSpace: 'nowrap' }}>
            {
              filterSuggestions.slice(0, maxSuggestions).map((sug, i) => (
                <BadgeCustom
                  // foreground={header.foreground}
                  // background={header.background}
                  key={`badge-suggestions-input-${sug + i}`}
                  variant="secondary"
                  className="my-2 mx-2"
                  onClick={() => {
                    if (mask === 'currency') {
                      handleChangeCurrency(null, sug.value, sug.label);
                    } else if (sug.id) {
                      setValue(sug.value);
                      setSubmitValue(sug.id);
                      if (onSuggestionSubmit) {
                        continueWithSuggestion(sug.id);
                      }
                    } else {
                      setValue(sug.value);
                      setSubmitValue(sug.value);
                      if (onSuggestionSubmit) {
                        continueWithSuggestion(sug.value);
                      }
                    }

                    if (filtered) {
                      filterSuggestion(sug.value);
                    }

                    setInputFocus();
                  }}
                >
                  {sug.text || sug.value}
                </BadgeCustom>
              ))
            }
          </Container>
        )}

      <Form ref={form} onSubmit={(e) => continueAction(e)}>
        <Container fluid="lg">
          <RobotBox background={input.background} fluid="lg" className="px-3 py-3 py-md-4 py-lg-6 align-items-end">
            <Col>
              <Row>
                <Col xs="auto" className="d-none d-md-block">
                  <Avatar
                    src={dialog.userAvatar}
                    rounded
                    size="sm"
                    title="MA"
                    className="mr-2"
                  />
                </Col>

                <Col className="px-0 px-md-3">
                  {mask === 'currency'
                    && (
                      <IntlCurrencyInput
                        placeholder={placeholder} inputMode="numeric"
                        autoFocus
                        value={value.raw} onChange={handleChangeCurrency} className="pr-auto form-control form-control-flush form-control-robot"
                        currency="BRL" config={currencyConfig}
                      />
                    )}

                  {(mask && mask !== 'currency')
                    && (
                      <InputMask
                        placeholder={placeholder}
                        mask={mask}
                        value={value}
                        onChange={handleChange}
                        className="pr-auto form-control-flush form-control-robot"
                        maskChar=""
                        autoFocus
                      >
                        {(inputProps) => (
                          <FormControlCustom {...inputProps}
                            foreground={input.foreground}
                            type={validation}
                            ref={inputRef}
                            className="pr-auto form-control-flush form-control-robot"
                            autoFocus
                            inputMode={numberMode ? 'numeric' : ''}
                          />
                        )}
                      </InputMask>
                    )}

                  {!mask
                    && (
                      <FormControlCustom
                        foreground={input.foreground}
                        value={value}
                        type={validation}
                        onChange={handleChange}
                        placeholder={placeholder}
                        as={textareaMode ? 'textarea' : ''}
                        rows={textareaMode ? 3 : undefined}
                        ref={inputRef}
                        className="pr-auto form-control-flush form-control-robot"
                        autoFocus
                      />
                    )}

                </Col>
              </Row>

            </Col>
            <Col xs="auto" className="mr-md-4 mr-xl-0 p-0 px-md-3">
              {optional
                ? (
                  <ButtonInputType button={input.button} size="md" type="submit" className="px-2 px-md-5 py-3">Continuar</ButtonInputType>
                )
                : (
                  <ButtonInputType button={input.button} size="md" type="submit" className="px-2 px-md-5 py-3" disabled={!isValid()}>Continuar</ButtonInputType>
                )}
            </Col>
          </RobotBox>
        </Container>
      </Form>
    </div>
  );
};

export default TextInput;
